import {
  AlgorithmInfo, EcosystemProjectDescriptionInfo,
  EcosystemProjectDetailInfo, GeneralSocialInfo,
  ProfileProjectDescriptionInfo,
  ProfileProjectDetailInfo, ProjectInvestorInfo,
  ProjectMainNetInfo,
  ProjectRoadMapInfo, ProjectStakeHolderInfo, ProjectTeamInfo,
} from '~/api/repository'
import { IMAGE } from '~/constants/image.constants'
import { SocialModel } from '~/types/model/general/social.model'
import { InvestorModel, StakeholderModel, TeamModel } from '~/types/model/stakeholder.model'
import { isEmptyList } from '~/utils/array.util'
import { formatAbbrDate } from '~/utils/date'
import { setComma } from '~/utils/number'
import { capitalizeFirstLetter } from '~/utils/string'

class AlgorithmModel {
  private readonly _type: string
  private readonly _name: string
  private readonly _description: string

  constructor (res: AlgorithmInfo) {
    this._type = res.TP
    this._name = res.NM
    this._description = res.DESC
  }

  get type () {
    return this._type
  }

  get name () {
    return this._name
  }

  get description () {
    return this._description
  }
}

class ProjectAboutModel {
  private readonly _name: string
  private readonly _oneLineDescription: string
  private readonly _launchingDate: string
  private readonly _officialLink: string
  private readonly _communityLink: Array<GeneralSocialInfo>
  private readonly _otherLink: Array<GeneralSocialInfo>
  private readonly _summary: string
  private readonly _projectGoal: string
  private readonly _projectTechnology: string
  private readonly _status: string
  private readonly _categoryList: string[]
  private readonly _logoUrl: string

  constructor (res: ProfileProjectDescriptionInfo) {
    this._name = res.NM
    this._oneLineDescription = res.ONEDESC
    this._launchingDate = res.LDT
    this._officialLink = res.OFFLK
    this._communityLink = res.COMMLK
    this._otherLink = res.OTHLK
    this._summary = res.SM
    this._projectGoal = res.PRJG
    this._projectTechnology = res.PRJTECH
    this._status = res.STAT
    this._categoryList = res.CATGLIST
    this._logoUrl = res.LGURL
  }

  get name () {
    return this._name
  }

  get oneLineDescription () {
    return this._oneLineDescription
  }

  get launchingDate () {
    return formatLaunchingDate(this._launchingDate)
  }

  get officialLink () {
    return this._officialLink
  }

  get communityLink () {
    if (isEmptyList(this._communityLink)) {
      return []
    }
    return this._communityLink.filter(community => community.LNK).map(community => {
      return new SocialModel(community.CH, community.SOCTP, community.LNK)
    })
  }

  get otherLink () {
    if (isEmptyList(this._otherLink)) {
      return []
    }
    return this._otherLink.filter(community => community.LNK).map(other => {
      return new SocialModel(other.CH, other.SOCTP, other.LNK)
    })
  }

  get summary () {
    return this._summary
  }

  get projectGoal () {
    return this._projectGoal
  }

  get projectTechnology () {
    return this._projectTechnology
  }

  get status () {
    return this._status
  }

  get categoryList () {
    return this._categoryList
  }

  get logoUrl () {
    return this._logoUrl || IMAGE.thumbnail.ecosystem
  }
}

class ProjectMainnetModel {
  private readonly _mainnetId: string
  private readonly _highestTpsSinceLaunch: string
  private readonly _theoreticalTps: string
  private readonly _blockTime: string
  private readonly _validatorNodeCount: string
  private readonly _validatorDelegationCount: string
  private readonly _launchingDate: string
  private readonly _hashAlgorithm: string
  private readonly _organizationStructure: string
  private readonly _contractLanguage: string[]
  private readonly _developmentStatus: string
  private readonly _algorithm: AlgorithmInfo

  constructor (res: ProjectMainNetInfo) {
    this._mainnetId = res.MNID
    this._highestTpsSinceLaunch = res.HTPSSLCH
    this._theoreticalTps = res.THTPS
    this._blockTime = res.BLKT
    this._validatorNodeCount = res.VALNDCNT
    this._validatorDelegationCount = res.VALDGCNT
    this._launchingDate = res.LCHDT
    this._hashAlgorithm = res.HAALGO
    this._organizationStructure = res.ORGSTR
    this._contractLanguage = res.CONTLANG
    this._developmentStatus = res.DEVSTAT
    this._algorithm = res.ALGO
  }

  get mainnetId () {
    return this._mainnetId
  }

  get highestTpsSinceLaunch () {
    return setComma(this._highestTpsSinceLaunch)
  }

  get theoreticalTps () {
    return setComma(this._theoreticalTps)
  }

  get blockTime () {
    return this._blockTime ? `About ${setComma(this._blockTime)} Secs` : '-'
  }

  get validatorNodeCount () {
    return setComma(this._validatorNodeCount)
  }

  get validatorDelegationCount () {
    return setComma(this._validatorDelegationCount)
  }

  get launchingDate () {
    return formatAbbrDate(this._launchingDate)
  }

  get hashAlgorithm () {
    return this._hashAlgorithm || '-'
  }

  get organizationStructure () {
    if (!this._organizationStructure) {
      return '-'
    }
    return capitalizeFirstLetter(this._organizationStructure)
  }

  get contractLanguage () {
    return this._contractLanguage.map(lang => capitalizeFirstLetter(lang)).join(', ') || '-'
  }

  get developmentStatus () {
    if (!this._developmentStatus) {
      return '-'
    }
    return capitalizeFirstLetter(this._developmentStatus)
  }

  get algorithm () {
    return new AlgorithmModel(this._algorithm)
  }
}

export class RoadmapModel {
  private readonly _roadmapId: string
  private readonly _name: string
  private readonly _detail: string
  private readonly _period: string
  private readonly _success: ProjectRoadMapInfo.SUCC

  constructor (res: ProjectRoadMapInfo) {
    this._roadmapId = res.RDMID
    this._name = res.NM
    this._detail = res.DET
    this._period = res.PRD
    this._success = res.SUCC
  }

  parsePeriod (period: string) {
    // TODO: 추후 번역 적용
    const [ year, quarter ] = period.split('-')
    if (year === '9999') {
      if (quarter === '90') {
        return 'TBD'
      }
      if (quarter === '80') {
        return 'In Progress'
      }
    } else {
      let quarterText
      switch (quarter) {
        case '50':
          quarterText = ''
          break
        case '45':
          quarterText = 'H2'
          break
        case '25':
          quarterText = 'H1'
          break
        default:
          quarterText = `Q${quarter[0]}`
      }
      return `${year} ${quarterText && ` ${quarterText}`}`
    }
  }

  get roadmapId () {
    return this._roadmapId
  }

  get name () {
    return this._name
  }

  get detail () {
    return this._detail
  }

  get period () {
    return this.parsePeriod(this._period)
  }

  get success () {
    return this._success === ProjectRoadMapInfo.SUCC.Y
  }
}

export class ProjectDetailModel {
  private readonly _about: ProfileProjectDescriptionInfo | EcosystemProjectDescriptionInfo
  private readonly _mainnetList: Array<ProjectMainNetInfo>
  private readonly _roadmapList: Array<ProjectRoadMapInfo>
  private readonly _stakeholders: ProjectStakeHolderInfo
  private readonly _team: ProjectTeamInfo
  private readonly _investor: ProjectInvestorInfo

  constructor (res: EcosystemProjectDetailInfo | ProfileProjectDetailInfo) {
    this._about = res.ABT
    this._mainnetList = res.MNLIST
    this._roadmapList = res.RDMLIST
    this._stakeholders = res.STHDR
    this._team = res.TM
    this._investor = res.IVTR
  }

  get about () {
    return new ProjectAboutModel(this._about)
  }

  get mainnetList () {
    return this._mainnetList.map(mainnet => new ProjectMainnetModel(mainnet))
  }

  get roadmapList () {
    return this._roadmapList.map(roadmap => new RoadmapModel(roadmap))
  }

  get stakeholders () {
    return new StakeholderModel(this._stakeholders)
  }

  get team () {
    return new TeamModel(this._team)
  }

  get investor () {
    return new InvestorModel(this._investor)
  }
}